import axios, { type AxiosInstance } from 'axios';
import axiosV2Instance from '@/Api/axiosV2Config';
import { useAuthStore as useAuthV1Store } from '@/Stores/AuthStore';
import env from '@/config/env';
import type {
  NewsCategory,
  NewsDetail,
  NewsEventInput,
  NewsEventsResponse,
  NewsFeedResponse,
  NewsPlatform,
  NewsSummary,
} from '../types/news.types';

/**
 * Client de LECTURE des nouveautés (`/news`), commun aux quatre plateformes.
 *
 * Le jeton dépend de la plateforme :
 * - outil pro, upsell, admin : session v2 — l'instance partagée
 *   `axiosV2Config` pose le jeton et le rafraîchit sur 401 ;
 * - gestion SAV : ancien jeton v1 (`AuthStore`), via une instance DÉDIÉE.
 *
 * Pourquoi ne pas réutiliser `authenticatedApi` pour la gestion SAV :
 * - il lit d'abord le store v2 et ne retombe sur le jeton v1 qu'à défaut ; ici
 *   on veut le jeton v1, sans ambiguïté ;
 * - son intercepteur déconnecte et redirige sur le moindre 401. Le garde des
 *   nouveautés est plus strict que le garde global (relecture en base, sans
 *   cache) : un 401 propre aux nouveautés ne doit pas expulser un gestionnaire
 *   que le reste de l'application accepte encore. Ici, une erreur reste une
 *   erreur de requête, que l'appelant ignore ou affiche.
 */
const savNewsClient = axios.create({
  baseURL: env.PUBLIC_API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

savNewsClient.interceptors.request.use((config) => {
  const token = useAuthV1Store.getState().access_token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function clientFor(platform: NewsPlatform): AxiosInstance {
  return platform === 'SAV' ? savNewsClient : axiosV2Instance;
}

export const newsReaderApi = {
  /** Fil paginé, dans l'ordre décidé par l'API. Sans le corps. `category` : un seul type (onglet « Mises à jour »). */
  getFeed: async (
    platform: NewsPlatform,
    page: number,
    limit: number,
    category?: NewsCategory,
  ): Promise<NewsFeedResponse> => {
    const { data } = await clientFor(platform).get<NewsFeedResponse>('/news', {
      params: { platform, page, limit, ...(category ? { category } : {}) },
    });
    return data;
  },

  /** Résumé d'arrivée : badge, diapos de la modale, nouveautés du bandeau. */
  getSummary: async (platform: NewsPlatform): Promise<NewsSummary> => {
    const { data } = await clientFor(platform).get<Partial<NewsSummary> | null>('/news/summary', {
      params: { platform },
    });
    // Ce résumé alimente le menu et le layout de quatre applications : on ne
    // fait confiance à la forme de la réponse qu'après l'avoir vérifiée (un
    // proxy qui répond 200 avec autre chose ne doit rien casser).
    const summary: Partial<NewsSummary> = data && typeof data === 'object' ? data : {};
    return {
      unreadCount: typeof summary.unreadCount === 'number' ? summary.unreadCount : 0,
      modal: Array.isArray(summary.modal) ? summary.modal : [],
      banner: Array.isArray(summary.banner) ? summary.banner : [],
    };
  },

  /** Une nouveauté et son corps. N'enregistre rien : `page_viewed` est déclaré à part. */
  getBySlug: async (platform: NewsPlatform, slug: string): Promise<NewsDetail> => {
    const { data } = await clientFor(platform).get<NewsDetail>(
      `/news/by-slug/${encodeURIComponent(slug)}`,
      { params: { platform } },
    );
    return data;
  },

  /** Déclare des événements de suivi (1 à 20 nouveautés). Idempotent. */
  recordEvents: async (platform: NewsPlatform, events: NewsEventInput[]): Promise<NewsEventsResponse> => {
    // Le serveur refuse tout champ inconnu : on ne transmet que `newsId` et `types`.
    const { data } = await clientFor(platform).post<NewsEventsResponse>('/news/events', {
      platform,
      events: events.map(({ newsId, types }) => ({ newsId, types })),
    });
    return data;
  },
};
